import axios from "axios";
import moment from "moment";
import { connect } from "react-redux";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useAlertMessage from "../../hooks/useAlertMessage";
import { API_URL } from "../../constants/Globals";

import SelectSequenceTypeModal from "./collections/SelectSequenceTypeModal";
import ConfigureQuizModal from "./collections/ConfigureQuizModal";
import ConfigureSurveyModal from "./collections/ConfigureSurveyModal";
import { addNewSequence } from "../../services/CollectionsSvc";
import BuildSequence from "./collections/BuildSequence";
import { fetchAiResponseLimitExceedStatus, fetchClientSubscriptionStatus } from "../../services/SubscriptionSvc";
import { getClientOrganization } from "../../services/NetworkSvc";

function SequencesList({ selNetworkId, networks }) {
  const [state, setState] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sequences, setSequences] = useState([]);
  const [setAlertMessage, getAlertMessage] = useAlertMessage();

  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [sequenceId, setSequenceId] = useState(null);
  const [isClientSubscriptionActive, setIsClientSubscriptionActive] = useState(false);
  const [isAiResponseLimitExceed, setIsAiResponseLimitExceed] = useState(false);

  const getIsSubscriptionActiveStatus = async (selNetworkId) => {
    const isActive = await fetchClientSubscriptionStatus(selNetworkId);
    setIsClientSubscriptionActive(isActive);
  }

  const getAILimitExceedStatus = async (selNetworkId) => {
    const groupId = await getClientOrganization(selNetworkId);
    // console.log('Group id for AI limit check:::::::::::::', groupId);
    if (groupId && groupId > 0) {
      const isAiRespLimitExceed = await fetchAiResponseLimitExceedStatus(groupId);
      // console.log('AI limit exceed status:::::::::::::::::', isAiReqLimitExceed, isAiRespLimitExceed);
      setIsAiResponseLimitExceed(isAiRespLimitExceed);
    } else {
      alert('Organiation not found for the selected network!!!');
    }
  }

  useEffect(() => {
    if (selNetworkId) {
      getIsSubscriptionActiveStatus(selNetworkId);
      getAILimitExceedStatus(selNetworkId);
      axios
        .get(`${API_URL}/collections?clientId=${selNetworkId}`)
        .then(({ data }) => {
          setLoading(false);
          setSequences(data.data);
        });
    }
  }, [selNetworkId, state]);

  const handleCreateSequenceClick = () => {
    if (isClientSubscriptionActive) {
      if (isAiResponseLimitExceed) {
        alert(`You've reached your limit for responses. Please upgrade or wait until limits reset`);
      } else {
        setShowTypeModal(true);
      }
    } else {
      window.location.href = `/subscription-renew`;
    }
  };

  const createSequence = async (payload) => {
    try {
      const response = await addNewSequence(payload);
      setSequenceId(response.data.data);
      setState(state + 1);
      if (payload.type === "Quiz") {
        window.location.href = `/sequences/${response.data.data}`;
      } else {
        setShowBuildModal(true);
      }
    } catch (error) {
      console.error("Failed to create sequence:", error);
      setAlertMessage("Failed to create sequence. Please try again.");
    }
  };

  const handleSaveQuizConfig = (config) => {
    const payload = {
      clientId: selNetworkId,
      name: config.quizName,
      type: "Quiz",
      sequenceCategories: config.quizCategory.map((category) => ({
        category: category?.category,
      })),
      advertisingType: config.advertising,
      questionTimer: config.questionTimer,
      questionOrder: config.questionOrder,
      showCorrectAnswers: config.showCorrectAnswers,
    };

    createSequence(payload);
    setShowQuizModal(false);
  };

  const handleSaveSurveyConfig = (config) => {
    const payload = {
      clientId: selNetworkId,
      name: config.surveyName,
      type: "Survey",
      timeToNextPoll: config.timeToNextPoll,
      advertisingType: config.advertisingType,
    };

    createSequence(payload);
    setShowSurveyModal(false);
  };

  const handleBackToTypeSelection = () => {
    setShowSurveyModal(false);
    setShowQuizModal(false);
    setShowTypeModal(true);
  };

  const handleNextFromBuild = (type) => {
    setShowBuildModal(false);
    window.location.href = `/sequences/${sequenceId}`;
  };

  return (
    <div className="segments">
      {getAlertMessage()}

      <div className="page-headline is-flex">
        <div>
          <strong>Content</strong>&nbsp;|&nbsp;Sequences
        </div>
        <button
          className="blue-button size-l"
          // onClick={() => setShowTypeModal(true)}
          onClick={handleCreateSequenceClick}
        >
          Create Sequence
        </button>
      </div>

      <div className="table-filters">
        <div className="table-pagination">{sequences.length} Sequences</div>
      </div>

      <div className="table-wrapper">
        <table
          className="table-body top-border"
          cellSpacing="0"
          cellPadding="0"
        >
          <tbody>
            {loading ? (
              <tr className="table-row">
                <td className="table-cell table-cell-fluid">
                  <em>Loading...</em>
                </td>
              </tr>
            ) : null}

            {!loading && !sequences.length ? (
              <tr className="table-row">
                <td className="table-cell table-cell-fluid">
                  <em>No sequences yet...</em>
                </td>
              </tr>
            ) : null}

            {sequences.map(({ id, name, isDraft, createdAt }, index) => (
              <tr key={id} className="table-row">
                <td className="table-cell table-cell-fluid">
                  <Link
                    to={`/sequences/${id}`}
                    className="text-button inherit-font"
                  >
                    {name}
                  </Link>
                  <div className="table-cell-labelled" width="150">
                    {isDraft ? "(Draft)" : " "}
                  </div>
                </td>
                <td className="table-cell table-cell-labelled" width="150">
                  <div>
                    Created
                    <br />
                    <strong>{moment(createdAt).format("MMM DD, Y")}</strong>
                  </div>
                </td>
                <td className="table-cell table-action" width="280">
                  <div className="inline-buttons">
                    <Link
                      to={`/sequences/${id}`}
                      className="blue-button bordered"
                    >
                      Edit
                    </Link>
                    <button
                      className="blue-button bordered"
                      onClick={() => {
                        if (window.confirm("Are you sure?")) {
                          axios
                            .delete(`${API_URL}/collections/${id}`)
                            .then(() => {
                              setState(state * 2);
                              setAlertMessage(
                                "The sequence was successfully deleted"
                              );
                            });
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SelectSequenceTypeModal
        show={showTypeModal}
        onClose={() => setShowTypeModal(false)}
        onSelectType={(type) => {
          setShowTypeModal(false);
          if (type === "Quiz") setShowQuizModal(true);
          if (type === "Survey") setShowSurveyModal(true);
        }}
      />

      <ConfigureQuizModal
        show={showQuizModal}
        onClose={() => setShowQuizModal(false)}
        onBack={handleBackToTypeSelection}
        onSave={handleSaveQuizConfig}
        selNetworkId={selNetworkId}
      />

      <ConfigureSurveyModal
        show={showSurveyModal}
        onClose={() => setShowSurveyModal(false)}
        onBack={handleBackToTypeSelection}
        onSave={handleSaveSurveyConfig}
      />

      <BuildSequence
        show={showBuildModal}
        onClose={() => setShowBuildModal(false)}
        onNext={handleNextFromBuild}
        selNetworkId={selNetworkId}
        sequenceId={sequenceId}
        orderNumber={0}
        isFromAddBranch={false}
        networks={networks}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  selNetworkId: state.networks.selNetworkId,
  networks: state.networks.networksList,
});

export default connect(mapStateToProps)(SequencesList);
